// v8.9.3 — Trail timeline view for a settled multiplier trade.
//
// Lazily fetches `/api/bot/trail-timeline/{contract_id}` and renders the
// chronological event chain:
//   • arm                  — first time the trail armed (peak ≥ arm%)
//   • floor_advance        — peak rose, floor moved up with it
//   • sell_trail           — trail floor breached (good or bad)
//   • sell_tp_stake        — +25% per-stake TP fired
//   • sell_sl_stake        — -10% per-stake SL fired
//   • sell_tp_balance      — balance TP backstop fired
//   • sell_sl_balance      — balance SL backstop fired
//
// Lets the user see exactly which trades the trail saved vs which it
// clipped early — essential data for tuning per-asset distance.
import { useEffect, useState } from "react";
import { Loader2, Lock, ArrowUpRight, CircleAlert, CircleCheck } from "lucide-react";
import { api } from "@/lib/api";

const KIND_META = {
  arm: { Icon: Lock, label: "Armed", tone: "text-vts-blue", bg: "bg-vts-blue/10 border-vts-blue/30" },
  floor_advance: { Icon: ArrowUpRight, label: "Floor advanced", tone: "text-vts-profit/80", bg: "bg-vts-profit/5 border-vts-profit/20" },
  sell_trail: { Icon: CircleCheck, label: "Sell · trail hit", tone: "text-vts-profit", bg: "bg-vts-profit/15 border-vts-profit/40" },
  sell_tp_stake: { Icon: CircleCheck, label: "Sell · +25% TP", tone: "text-vts-profit", bg: "bg-vts-profit/15 border-vts-profit/40" },
  sell_tp_balance: { Icon: CircleCheck, label: "Sell · balance TP", tone: "text-vts-profit", bg: "bg-vts-profit/15 border-vts-profit/40" },
  sell_sl_stake: { Icon: CircleAlert, label: "Sell · -10% SL", tone: "text-vts-loss", bg: "bg-vts-loss/15 border-vts-loss/40" },
  sell_sl_balance: { Icon: CircleAlert, label: "Sell · balance SL", tone: "text-vts-loss", bg: "bg-vts-loss/15 border-vts-loss/40" },
};

const fmtPct = (n) => (n == null ? "—" : `${Number(n) >= 0 ? "+" : ""}${Number(n).toFixed(1)}%`);
const fmtUsd = (n) => (n == null ? "—" : `${Number(n) >= 0 ? "+" : ""}$${Math.abs(Number(n)).toFixed(2)}`);
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
};

export function TrailTimeline({ contractId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get(`/bot/trail-timeline/${contractId}`);
        if (cancelled) return;
        setEvents(Array.isArray(data?.events) ? data.events : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load timeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  if (loading) {
    return (
      <div
        data-testid={`trail-timeline-loading-${contractId}`}
        className="flex items-center gap-2 text-[10px] font-mono text-vts-silver/50 px-3 py-2"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading trail timeline…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-[10px] font-mono text-vts-loss px-3 py-2">
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div
        data-testid={`trail-timeline-empty-${contractId}`}
        className="text-[10px] font-mono text-vts-silver/50 px-3 py-2 italic"
      >
        No trail events recorded for this trade — never reached the arm threshold (5%) before close.
      </div>
    );
  }

  return (
    <div
      data-testid={`trail-timeline-${contractId}`}
      className="pl-3 pr-3 py-2 space-y-1.5"
    >
      <div className="text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono mb-1">
        Trail timeline · {events.length} {events.length === 1 ? "event" : "events"}
      </div>
      <ol className="space-y-1">
        {events.map((e, idx) => {
          const meta = KIND_META[e.kind] || {
            Icon: CircleAlert,
            label: e.kind,
            tone: "text-vts-silver",
            bg: "bg-vts-surface border-vts-line",
          };
          const Icon = meta.Icon;
          const isSell = (e.kind || "").startsWith("sell_");
          return (
            <li
              key={idx}
              data-testid={`trail-event-${contractId}-${e.kind}-${idx}`}
              className={`flex items-center gap-2 text-[10px] font-mono px-2 py-1 rounded border ${meta.bg}`}
            >
              <Icon className={`h-3 w-3 shrink-0 ${meta.tone}`} />
              <span className={`${meta.tone} uppercase tracking-wider w-[120px] shrink-0`}>
                {meta.label}
              </span>
              <span className="text-vts-silver/70 tabular-nums w-[60px]">
                {fmtTime(e.ts)}
              </span>
              <span className="text-vts-silver/85 tabular-nums">
                peak {fmtPct(e.peak_pct)}
              </span>
              <span className="text-vts-silver/30">·</span>
              <span className="text-vts-silver/85 tabular-nums">
                floor {fmtPct(e.floor_pct)}
              </span>
              {e.prev_floor_pct != null && e.kind === "floor_advance" && (
                <span className="text-vts-silver/55 tabular-nums">
                  (↑ from {fmtPct(e.prev_floor_pct)})
                </span>
              )}
              <span className="text-vts-silver/30">·</span>
              <span
                className={`tabular-nums ${
                  Number(e.profit_usd || 0) >= 0 ? "text-vts-profit" : "text-vts-loss"
                }`}
              >
                {fmtUsd(e.profit_usd)} ({fmtPct(e.profit_pct)})
              </span>
              {isSell && e.reason && (
                <span className="text-vts-silver/50 truncate max-w-[260px]" title={e.reason}>
                  · {e.reason}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default TrailTimeline;
